import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { InAppNotificationType } from 'src/app/in-app-notifications/models/in-app-notification-type.enum';
import { InAppNotification } from 'src/app/in-app-notifications/models/in-app-notification.model';
import { combine } from 'src/app/shared/utilities/route';
import { ApplicationEnum } from 'src/app/shared/enums/application.enum';
import { InAppNotificationSettings } from 'src/app/in-app-notifications/models/in-app-notification-settings.model';
import { InAppNotificationFilter } from 'src/app/in-app-notifications/models/in-app-notification-filter.model';
import { KeyValuePair } from '../shared/models/key-value-pair.model';
import { UnreadInAppNotificationCounts } from 'src/app/in-app-notifications/models/unread-in-app-notification-counts.model';
import { CorporateDirectoryUser } from 'src/app/corporate-directory/models/corporate-directory-user.model';

@Injectable({
    providedIn: 'root'
})
export class InAppNotificationsService {
    private readonly coreApiPath: string;

    constructor(private readonly http: HttpClient, @Inject(APP_CONFIG) settings: Settings) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'in-app-notifications');
    }

    getInAppNotifications(filter: InAppNotificationFilter): Observable<InAppNotification[]> {
        return this.http.post<InAppNotification[]>(`${this.coreApiPath}`, {
            ...filter,
            applicationId: ApplicationEnum.NovaWeb
        });
    }

    getUnreadInAppNotificationsCount(): Observable<UnreadInAppNotificationCounts> {
        return this.http.get<UnreadInAppNotificationCounts>(
            `${this.coreApiPath}/${ApplicationEnum.NovaWeb}/unread-count`
        );
    }

    getUnreadInAppNotificationsCountForDelegation(userId: string): Observable<UnreadInAppNotificationCounts> {
        return this.http.get<UnreadInAppNotificationCounts>(
            `${this.coreApiPath}/${ApplicationEnum.NovaWeb}/${userId}/unread-count`
        );
    }

    getUnreadInAppNotificationsTabCounters(): Observable<Array<KeyValuePair<InAppNotificationType, number>>> {
        return this.http.get<Array<KeyValuePair<InAppNotificationType, number>>>(
            `${this.coreApiPath}/${ApplicationEnum.NovaWeb}/unread-tab-counters`
        );
    }

    getUnreadInAppNotificationsTabCountersForDelegation(
        userId: string
    ): Observable<Array<KeyValuePair<InAppNotificationType, number>>> {
        return this.http.get<Array<KeyValuePair<InAppNotificationType, number>>>(
            `${this.coreApiPath}/${ApplicationEnum.NovaWeb}/${userId}/unread-tab-counters`
        );
    }

    hasOverdueTasksCreatedByMe(): Observable<boolean> {
        return this.http.get<boolean>(`${this.coreApiPath}/has-overdue-tasks-created-by-me`);
    }

    getInAppNotificationById(notificationId: number): Observable<InAppNotification> {
        return this.http.get<InAppNotification>(`${this.coreApiPath}/${notificationId}`);
    }

    updateInAppNotificationSettings(
        notificationId: number,
        aspNetUserId: string,
        settings: InAppNotificationSettings
    ): Observable<InAppNotification> {
        return this.http.put<InAppNotification>(
            `${this.coreApiPath}/${notificationId}/${aspNetUserId}/settings`,
            settings
        );
    }

    markAllAsRead(parentInAppNotificationTypeId: InAppNotificationType, aspNetUserId: string) {
        return this.http.put(
            `${this.coreApiPath}/settings/mark-all-as-read/${parentInAppNotificationTypeId}/${aspNetUserId}`,
            null
        );
    }

    getInAppNotificationsUser(userId: string): Observable<CorporateDirectoryUser> {
        return this.http.get<CorporateDirectoryUser>(`${this.coreApiPath}/user/${userId}`);
    }
}
